import { useEffect, useState } from "react";
import { Phone, Clock, MapPin, Send, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { useSite } from "@/context/SiteContext";
import { UI } from "@/i18n";
import api, { formatApiErrorDetail } from "@/lib/api";
import Reveal from "./Reveal";

export default function Contact() {
  const { content, t } = useSite();
  const [form, setForm] = useState({ name: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const c = content?.contact_section;
  const wa = content?.whatsapp;

  useEffect(() => {
    const handler = (e) => setForm((f) => ({ ...f, message: e.detail || f.message }));
    window.addEventListener("ct-prefill-quote", handler);
    return () => window.removeEventListener("ct-prefill-quote", handler);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/quotes", form);
      toast.success(t(UI.contact.success));
      setForm({ name: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t(UI.contact.error));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="py-24 sm:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-start">
        <Reveal>
          <div className="flex items-center gap-3 mb-4">
            <span className="stitch-x w-10 text-brand-blue" />
            <span className="font-mono2 text-xs uppercase tracking-[0.3em] text-brand-blue">{t(c?.eyebrow)}</span>
          </div>
          <h2 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">{t(c?.title)}</h2>
          <p className="mt-5 text-white/70 max-w-md">{t(c?.subtitle)}</p>

          <a
            href={`tel:${content?.phone}`}
            data-testid="contact-call-link"
            className="mt-8 flex items-center gap-4 group w-fit"
          >
            <span className="flex items-center justify-center w-14 h-14 rounded-full bg-brand-red group-hover:scale-110 transition-transform">
              <Phone size={22} />
            </span>
            <span>
              <span className="block text-xs font-mono2 uppercase tracking-widest text-white/60">{t(UI.contact.callNow)}</span>
              <span className="font-mono2 font-bold text-2xl sm:text-3xl group-hover:text-brand-orange transition-colors">
                {content?.phone}
              </span>
            </span>
          </a>

          {wa && (
            <a
              href={`https://wa.me/${wa}`}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-whatsapp"
              className="mt-4 inline-flex items-center gap-2 border border-white/20 hover:border-[#25D366] hover:text-[#25D366] rounded-full px-5 py-2.5 text-sm font-semibold transition-colors"
            >
              <MessageCircle size={16} /> WhatsApp
            </a>
          )}

          <div className="mt-8 space-y-3 text-sm text-white/70">
            <p className="flex items-center gap-3">
              <Clock size={15} className="text-brand-orange" /> <span className="font-mono2">{t(content?.hours)}</span>
            </p>
            <p className="flex items-center gap-3">
              <MapPin size={15} className="text-brand-red" /> {t(content?.service_area)}
            </p>
          </div>

          {(content?.service_areas || []).length > 0 && (
            <div className="mt-8">
              <p className="font-mono2 text-xs uppercase tracking-widest text-white/50 mb-3">{t(UI.areas.title)}</p>
              <ul className="flex flex-wrap gap-2" data-testid="service-areas">
                {content.service_areas.map((a, i) => (
                  <li key={i} className="text-xs font-semibold border border-white/15 rounded-full px-3 py-1.5 text-white/75">
                    {t(a)}
                  </li>
                ))}
              </ul>
              <iframe
                title="Service area map"
                src={`https://www.google.com/maps?q=${encodeURIComponent(t(content.service_area))}&output=embed`}
                data-testid="service-area-map"
                loading="lazy"
                className="mt-5 w-full h-44 rounded-xl border border-white/10 grayscale-[0.3] contrast-[1.05]"
              />
            </div>
          )}
        </Reveal>

        <Reveal delay={0.15}>
          <form
            onSubmit={submit}
            data-testid="quote-form"
            className="rounded-xl border border-white/10 bg-brand-surface p-8 space-y-5 shadow-[0_40px_80px_-40px_rgba(0,0,0,0.8)]"
          >
            <h3 className="font-display uppercase text-2xl">{t(UI.contact.formTitle)}</h3>
            <input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder={t(UI.contact.name)}
              data-testid="quote-form-name"
              className="w-full bg-white/5 border border-white/15 rounded-lg px-4 py-3.5 text-sm placeholder:text-white/40 focus:outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30 transition-all"
            />
            <input
              required
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder={t(UI.contact.phone)}
              data-testid="quote-form-phone"
              className="w-full bg-white/5 border border-white/15 rounded-lg px-4 py-3.5 text-sm placeholder:text-white/40 focus:outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30 transition-all"
            />
            <textarea
              rows={4}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder={t(UI.contact.message)}
              data-testid="quote-form-message"
              className="w-full bg-white/5 border border-white/15 rounded-lg px-4 py-3.5 text-sm placeholder:text-white/40 focus:outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30 transition-all resize-none"
            />
            <button
              type="submit"
              disabled={sending}
              data-testid="quote-form-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-brand-orange hover:bg-brand-amber disabled:opacity-60 text-brand-navy font-bold py-4 rounded-lg transition-all hover:scale-[1.01]"
            >
              {sending ? t(UI.contact.sending) : t(UI.contact.send)}
              {!sending && <Send size={16} />}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
